import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/content/site";

// Mobilní rozbalovací blok — na desktopu (md+) je obsah vždy viditelný a tlačítko skryté.
const FooterDisclosure = ({ mobileTitle, desktopTitle, testId, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="text-left">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={testId}
        className="md:hidden flex items-center justify-between w-full py-1 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium"
      >
        {mobileTitle}
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
          aria-hidden="true"
        />
      </button>
      <h2 className="hidden md:block text-xs uppercase tracking-[0.25em] text-[#737373] font-medium mb-5">{desktopTitle}</h2>
      <div className={open ? "block mt-3" : "hidden md:block"}>{children}</div>
    </div>
  );
};

export const Footer = () => {
  const year = new Date().getFullYear();
  const [legalOpen, setLegalOpen] = useState(false);

  return (
    <footer data-testid="footer" className="bg-white border-t border-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-8 md:py-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 text-center md:text-left">
        <div>
          <p className="font-display text-xl text-[#252525]">Daniel Pekař</p>
          <p className="text-sm text-[#737373] mt-2 md:mt-3 leading-relaxed max-w-xs mx-auto md:mx-0">
            Finanční poradenství — hypotéky, úvěry, investice a pojištění. Srozumitelně, osobně a bez zbytečných složitostí.
          </p>
        </div>

        <FooterDisclosure mobileTitle="Rychlé odkazy" desktopTitle="Navigace" testId="footer-nav-toggle">
          <ul className="space-y-2 md:space-y-3">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href.startsWith("#") ? `/${link.href}` : link.href}
                  data-testid={`footer-link-${link.href.replace(/^[#/]+/, "")}`}
                  className="text-sm text-[#4A4A4A] hover:text-[#B69A58] transition-colors duration-300"
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li className="hidden md:block">
              <Link
                to="/ochrana-osobnich-udaju"
                data-testid="footer-privacy-link"
                className="text-sm text-[#4A4A4A] hover:text-[#B69A58] transition-colors duration-300"
              >
                Ochrana osobních údajů
              </Link>
            </li>
          </ul>
        </FooterDisclosure>

        <div className="text-center md:text-left">
          <h2 className="text-xs uppercase tracking-[0.25em] text-[#737373] font-medium mb-3 md:mb-5">Kontakt</h2>
          <ul className="space-y-2 md:space-y-3 text-sm text-[#4A4A4A]">
            <li>
              <a href={CONTACT.phoneHref} data-testid="footer-phone" className="hover:text-[#B69A58] transition-colors duration-300">
                {CONTACT.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="hover:text-[#B69A58] transition-colors duration-300">
                {CONTACT.email}
              </a>
            </li>
            <li>
              <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="hover:text-[#B69A58] transition-colors duration-300">
                WhatsApp
              </a>
            </li>
            <li data-testid="footer-address">Působiště: {CONTACT.address}</li>
            <li className="md:hidden pt-1">
              <Link
                to="/ochrana-osobnich-udaju"
                data-testid="footer-privacy-mobile"
                className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300"
              >
                Ochrana osobních údajů
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#F0F0F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-4 md:py-8">
          <button
            type="button"
            onClick={() => setLegalOpen(!legalOpen)}
            aria-expanded={legalOpen}
            data-testid="footer-legal-toggle"
            className="md:hidden flex items-center justify-center gap-2 w-full text-xs uppercase tracking-[0.25em] text-[#737373] font-medium"
          >
            Právní informace a oprávnění
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-300 ${legalOpen ? "rotate-180" : ""}`}
              strokeWidth={1.5}
              aria-hidden="true"
            />
          </button>
          <div
            className={`${legalOpen ? "block" : "hidden"} md:grid md:grid-cols-3 gap-x-10 gap-y-4 md:gap-y-6 text-xs text-[#737373] leading-relaxed text-center ${legalOpen ? "space-y-4 md:space-y-0 mt-4 md:mt-0" : ""}`}
            data-testid="footer-legal"
          >
            <p>
              {CONTACT.name} · IČO: {CONTACT.ico}
              <br />
              Sídlo: {CONTACT.sidlo}
              <br />
              Partner: M&M Finance (skupina M&M Reality)
            </p>
            <p>
              Samostatný zprostředkovatel zapsaný v evidenci České národní banky —{" "}
              <a
                href="https://www.cnb.cz/cnb/jerrs"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-cnb-register"
                className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300"
              >
                ověřit zápis v registru ČNB
              </a>
              <br />
              Odborná způsobilost: odborná zkouška EFPA ČR — distribuce pojištění (program akreditovaný ČNB)
            </p>
            <p>
              Dozorující orgán: Česká národní banka, Na Příkopě 28, 115 03 Praha 1
              <br />
              Mimosoudní řešení sporů: Finanční arbitr, Legerova 1581/69, 110 00 Praha 1 ·{" "}
              <a
                href="https://www.finarbitr.cz"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-finarbitr"
                className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300"
              >
                finarbitr.cz
              </a>
            </p>
          </div>
        </div>
      </div>
      <div className="border-t border-[#F0F0F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-5 pb-24 md:pb-8 grid sm:grid-cols-3 gap-x-10 gap-y-3 md:gap-y-4 items-center text-center text-xs text-[#737373]">
          <p>© {year} Daniel Pekař. Všechna práva vyhrazena.</p>
          <p>
            Měření a reklamní cookies pouze se souhlasem ·{" "}
            <button
              type="button"
              data-testid="footer-cookie-settings"
              onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
              className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300"
            >
              Nastavení cookies
            </button>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <img
              src="/images/logo-webdesignmartina.png"
              alt="Martina Webdesign"
              width={480}
              height={272}
              loading="lazy"
              className="h-9 w-auto credit-logo-light"
            />
            <img
              src="/images/logo-webdesignmartina-dark.png"
              alt="Martina Webdesign"
              width={150}
              height={150}
              loading="lazy"
              className="h-[53px] w-auto credit-logo-dark"
            />
            <span className="w-px h-8 bg-[#E5E5E5]" aria-hidden="true" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#737373]">Webdesign & vývoj</p>
            <a
              href="https://webdesignmartina.cz"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-credit-link"
              className="text-xs uppercase tracking-[0.2em] text-[#252525] hover:text-[#B69A58] transition-colors duration-300"
            >
              www.webdesignmartina.cz
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
